import { container } from 'tsyringe';
import Firebase from '@base/services/firebase/firebase';
import LocalStorage from '@base/features/storage/localStorage';
import AuthService from '@base/features/auth/model/authService';
import HelpService from '@base/features/help/model/helpService';
import LanguageService from '@base/features/i18n/model/languageService';
import PreferencesService from '@base/features/preferences/model/preferencesService';
import ForumService from '@base/features/forum/model/forumService';
import TournamentsService from '@base/features/tournaments/model/tournamentsService';
import TableSettingsService from '@base/features/table/tableSettingsService';
import { getRequiredEnv } from '@base/utils/helpers';
import { MessagesService } from '@base/features/chat/messagesService';
import { MessagesRepository } from '@base/features/chat/messagesRepository';
import { Messages } from '@base/features/chat/messages';
import LadderService from '@base/features/ladder/model/LadderService';
import ChallengeService from '@base/features/ladder/model/ChallengeService';
import ModalProvider from '@base/features/game/model/modalProvider';
import { createBrowserHistory, createHashHistory } from 'history';
import TicketsService from '@base/features/tickets/model/ticketsService';
import ApiClient from '@base/features/api/model/apiClient';
import InviteService from './features/game/model/inviteService';
import GameService from '@base/features/game/model/gameService';
import { IGameProvider, ITable } from '@base/features/game/model/interfaces';
import PlayersOnlineRepository from '@base/features/player/model/playersOnlineRepository';
import { BasePreferences } from './features/preferences/model/types';
import BaseTablesRepository from './features/game/model/baseTableRepository';
import GamesHistoryService from './features/user/model/gamesHistoryService';
import SoundProvider from './features/game/model/soundProvider';
import SocketClient from './features/socket/socketClient';
import ThemeService from './features/theme/themeService';
import UserService from './features/user/model/userService';
import LobbyService from './features/lobby/model/lobbyService';
import Message from './features/message/message';
import CreateTableService from './features/createTable/model/createTableService';
import ImageProvider from './features/image/imageProvider';

export function initModules<P extends BasePreferences = BasePreferences, T extends ITable = ITable>(
  gameProvider: IGameProvider<P, T>,
  tableRepository: BaseTablesRepository<T>,
  imageProvider: ImageProvider<any>,
  defaultPreferences: P
) {
  const history =
    process.env.NX_USE_HASH_ROUTER === 'true'
      ? // hash history is needed for Electron build
        createHashHistory()
      : createBrowserHistory({
          basename: getRequiredEnv('NX_APP_URL_PATH'),
          // basename: process.env.PUBLIC_URL,
        });

  const apiURL = getRequiredEnv('NX_API_ENDPOINT');
  const GAME_ID = gameProvider.GAME_ID;
  const APP_ID = gameProvider.APP_ID;

  const messages = new Messages();
  const messagesRepository = new MessagesRepository();
  const firebaseService = new Firebase();
  const modalProvider = new ModalProvider();
  const localStorage = new LocalStorage();
  const socketClient = new SocketClient();
  const message = new Message();

  const languageService = new LanguageService(localStorage);
  const preferencesService = new PreferencesService<P>(localStorage, defaultPreferences);
  const soundProvider = new SoundProvider(preferencesService);

  const apiClient = new ApiClient(apiURL, localStorage);
  const ladderService = new LadderService(apiClient, GAME_ID);
  const challengeService = new ChallengeService(apiClient, GAME_ID);

  const userService = new UserService(apiClient, localStorage, modalProvider, GAME_ID);
  const tournamentsService = new TournamentsService(GAME_ID, apiClient);
  const helpService = new HelpService(GAME_ID, apiClient);
  const forumService = new ForumService(GAME_ID, apiClient, localStorage);
  const themeService = new ThemeService(localStorage);
  const playersOnlineRepository = new PlayersOnlineRepository<P, T>(socketClient, soundProvider, localStorage);

  const messagesService = new MessagesService<P, T>(
    GAME_ID,
    playersOnlineRepository,
    tableRepository,
    preferencesService,
    messagesRepository,
    messages,
    socketClient,
    modalProvider
  );

  const ticketsService = new TicketsService(
    apiClient,
    languageService,
    playersOnlineRepository,
    modalProvider,
    messagesService
    /* userService */
  );

  const authService = new AuthService(
    APP_ID,
    GAME_ID,
    apiClient,
    socketClient,
    /* userService, */
    firebaseService,
    localStorage
  );

  const inviteService = new InviteService(
    socketClient,
    messagesService,
    playersOnlineRepository,
    modalProvider,
    soundProvider,
    tableRepository,
    history,
    ticketsService
  );

  const tableSettingsRepository = new TableSettingsService(localStorage, gameProvider.DEFAULT_TABLE_SETTINGS);

  const gameService = new GameService(
    socketClient,
    tableRepository,
    playersOnlineRepository,
    messagesService,
    modalProvider,
    soundProvider
  );

  const lobbyService = new LobbyService(
    socketClient,
    playersOnlineRepository,
    tableRepository,
    messagesService,
    inviteService,
    gameProvider,
    preferencesService,
    languageService,
    soundProvider,
    modalProvider,
    ticketsService,
    gameService,
    authService
  );

  const gamesHistoryService = new GamesHistoryService(GAME_ID, apiClient);

  socketClient.subscirbe(
    messagesService,
    tableRepository,
    lobbyService,
    inviteService,
    playersOnlineRepository,
    gameService,
    ticketsService
  );

  socketClient.onConnectionError = lobbyService.logout.bind(lobbyService);

  const createTableService = new CreateTableService(socketClient, tableSettingsRepository, playersOnlineRepository);

  container.register('inviteService', { useValue: inviteService });
  container.register('createTableService', { useValue: createTableService });
  container.register('gamesHistoryService', { useValue: gamesHistoryService });
  container.register('message', { useValue: message });
  container.register('history', { useValue: history });
  container.register('firebaseService', { useValue: firebaseService });
  container.register('gameProvider', { useValue: gameProvider });
  container.register('lobbyService', { useValue: lobbyService });
  container.register('localStorage', {
    useValue: localStorage,
  });
  container.register('gameService', { useValue: gameService });
  container.register('languageService', { useValue: languageService });
  container.register('userService', { useValue: userService });
  container.register('tournamentsService', { useValue: tournamentsService });
  container.register('socketClient', { useValue: socketClient });
  container.register('authService', { useValue: authService });
  container.register('tableSettingsRepository', {
    useValue: tableSettingsRepository,
  });
  container.register('apiClient', { useValue: apiClient });
  container.register('preferencesService', { useValue: preferencesService });
  container.register('themeService', { useValue: themeService });
  container.register('playersOnlineRepository', {
    useValue: playersOnlineRepository,
  });
  container.register('tableRepository', { useValue: tableRepository });
  container.register('modalProvider', { useValue: modalProvider });
  container.register('messagesRepository', { useValue: messagesRepository });
  container.register('messagesService', { useValue: messagesService });
  container.register('ladderService', { useValue: ladderService });
  container.register('challengeService', { useValue: challengeService });
  container.register('ticketsService', { useValue: ticketsService });
  container.register('helpService', { useValue: helpService });
  container.register('forumService', { useValue: forumService });
  container.register('imageProvider', { useValue: imageProvider });
}
